import React, { useState } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, CircularProgress, Alert, Stack, useMediaQuery, useTheme,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import ReportExportMenu from './ReportExportMenu';

const fontSize = { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' };

export const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 2 }).format(amount || 0);

export const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

const TONES = {
  good: { light: ['#e8f5e9', '#2e7d32'], dark: ['rgba(76,175,80,0.12)', '#4caf50'] },
  bad: { light: ['#ffebee', '#c62828'], dark: ['rgba(239,83,80,0.12)', '#ef5350'] },
  warn: { light: ['#fff3e0', '#e65100'], dark: ['rgba(255,152,0,0.12)', '#ffa726'] },
};

/**
 * A report table in the v3 report style: # column, rows, a totals row, paging - or cards on a phone unless
 * `tableOnPhone` (wide month tables scroll sideways instead).
 *
 * columns: [{ key, label, align, render(row), total(totals), color }]; the first column names the row on a phone.
 */
export const ReportTable = ({
  title, columns, rows, rowKey, totals, totalsLabel, loading, emptyMessage = 'No records found', onRowClick, rowSx,
  pageSize = 25, tableOnPhone = false, numbered = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md')) && !tableOnPhone;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const shown = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const totalsBg = isDark ? 'rgba(76,175,80,0.08)' : '#e8f5e9';
  const headSx = { fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' };
  const cellSx = { fontSize: '13px', py: 1, whiteSpace: 'nowrap' };
  const cell = (c, row) => (c.render ? c.render(row) : row[c.key] ?? '-');
  const heading = title && <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 1 }}>{title}</Typography>;

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
  if (rows.length === 0) {
    return (
      <Box sx={{ mb: 2 }}>
        {heading}
        <Paper sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>{emptyMessage}</Typography>
        </Paper>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        {heading}
        {shown.map((row, idx) => (
          <Card key={rowKey(row, idx)} variant="outlined" onClick={onRowClick ? () => onRowClick(row) : undefined}
            sx={{ cursor: onRowClick ? 'pointer' : 'default', ...(rowSx ? rowSx(row) : {}) }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography component="div" sx={{ fontSize: fontSize.sm, fontWeight: 600, mb: 1 }}>{cell(columns[0], row)}</Typography>
              {columns.slice(1).map((c) => (
                <Box key={c.key} display="flex" justifyContent="space-between" gap={2}>
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>{c.label}</Typography>
                  <Typography component="div" sx={{ fontSize: fontSize.xs, fontWeight: 600, textAlign: 'right' }}>{cell(c, row)}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        ))}
        {totals && (
          <Card sx={{ bgcolor: totalsBg }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, mb: 1 }}>{totalsLabel}</Typography>
              {columns.filter((c) => c.total).map((c) => (
                <Box key={c.key} display="flex" justifyContent="space-between">
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>{c.label}</Typography>
                  <Typography sx={{ fontSize: fontSize.xs, fontWeight: 700 }}>{c.total(totals)}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}
        {rows.length > rowsPerPage && (
          <TablePagination component="div" count={rows.length} page={page} onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage} rowsPerPageOptions={[rowsPerPage]} />
        )}
      </Stack>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      {heading}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {numbered && <TableCell sx={{ ...headSx, width: 40 }}>#</TableCell>}
                {columns.map((c) => (
                  <TableCell key={c.key} align={c.align} sx={{ ...headSx, ...(c.color ? { color: c.color } : {}) }}>{c.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((row, idx) => (
                <TableRow key={rowKey(row, idx)} hover onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default', ...(rowSx ? rowSx(row) : {}) }}>
                  {numbered && <TableCell sx={{ ...cellSx, color: 'text.secondary' }}>{page * rowsPerPage + idx + 1}</TableCell>}
                  {columns.map((c) => <TableCell key={c.key} align={c.align} sx={cellSx}>{cell(c, row)}</TableCell>)}
                </TableRow>
              ))}
              {totals && (
                <TableRow sx={{ bgcolor: totalsBg }}>
                  <TableCell colSpan={numbered ? 2 : 1} sx={{ fontSize: '13px', fontWeight: 700, py: 1.5, whiteSpace: 'nowrap' }}>{totalsLabel}</TableCell>
                  {columns.slice(1).map((c) => (
                    <TableCell key={c.key} align={c.align} sx={{ fontSize: '13px', fontWeight: 700, py: 0.75, whiteSpace: 'nowrap' }}>
                      {c.total ? c.total(totals) : ''}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {rows.length > 10 && rows.length > rowsPerPage && (
          <TablePagination component="div" count={rows.length} page={page}
            onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]} />
        )}
      </Paper>
    </Box>
  );
};

/**
 * The v3 report layout (as Customer Advances / Receivables Aging): title and subtitle, the Export menu (PDF / CSV, BE-14b) and
 * refresh, a filter bar, summary cards, then the report table (ReportTable props pass through) and `footer` below it.
 *
 * cards: [{ label, value, tone: 'good' | 'bad' | 'warn' }]. exportType + exportParams drive the export.
 */
const ReportPage = ({
  title, subtitle, exportType, exportParams, onRefresh, error, onErrorClose, onError, filters, cards = [],
  children, footer, ...table
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ pb: isMobile ? 2 : 0, width: 0, minWidth: '100%' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography sx={{ fontSize: isMobile ? '1rem' : '1.125rem', fontWeight: 600, color: 'secondary.main' }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>{subtitle}</Typography>}
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          {exportType && <ReportExportMenu reportType={exportType} params={exportParams} onError={onError} />}
          {onRefresh && (
            <Button size="small" variant="outlined" onClick={onRefresh} sx={{ minWidth: 36 }}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={onErrorClose}>{error}</Alert>}

      {filters && <Paper sx={{ p: 2, mb: 2 }}>{filters}</Paper>}

      {!table.loading && cards.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {cards.map((c) => {
            const [bg, color] = c.tone ? TONES[c.tone][isDark ? 'dark' : 'light'] : [undefined, 'text.primary'];
            return (
              <Grid item xs={6} sm={4} md={Math.max(2, Math.floor(12 / cards.length))} key={c.label}>
                <Card sx={{ bgcolor: bg, height: '100%' }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, mb: 0.5 }}>
                      {c.label}
                    </Typography>
                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color }}>{c.value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {children}

      <ReportTable {...table} />

      {!table.loading && footer}
    </Box>
  );
};

export default ReportPage;
